<template>
  <div class="helper-credit-page">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>帮扶分管理</span>
        </div>
      </template>

      <!-- 搜索栏 -->
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="用户ID">
          <el-input v-model="searchForm.userId" placeholder="请输入用户ID" clearable />
        </el-form-item>
        <el-form-item label="手机号">
          <el-input v-model="searchForm.mobile" placeholder="请输入手机号" clearable />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">搜索</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 数据表格 -->
      <el-table :data="tableData" v-loading="loading" border stripe>
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="userId" label="用户ID" width="100" />
        <el-table-column prop="userName" label="用户名" width="120" />
        <el-table-column prop="mobile" label="手机号" width="130" />
        <el-table-column prop="balance" label="当前余额" width="120">
          <template #default="{ row }">
            <span class="balance">{{ row.balance?.toFixed(2) || '0.00' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="totalEarned" label="累计获得" width="120">
          <template #default="{ row }">
            <span class="earned">{{ row.totalEarned?.toFixed(2) || '0.00' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="totalUsed" label="已使用" width="120">
          <template #default="{ row }">
            <span class="used">{{ row.totalUsed?.toFixed(2) || '0.00' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="180" />
        <el-table-column label="操作" fixed="right" width="150">
          <template #default="{ row }">
            <el-button type="primary" link @click="viewTransactions(row)">交易记录</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        v-model:current-page="pagination.page"
        v-model:page-size="pagination.pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        class="pagination"
      />
    </el-card>

    <!-- 交易记录弹窗 -->
    <el-dialog v-model="transactionDialogVisible" title="交易记录" width="800px">
      <el-table :data="transactionList" v-loading="transactionLoading">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="type" label="类型" width="100">
          <template #default="{ row }">
            <el-tag :type="row.type === 'earn' ? 'success' : 'warning'">
              {{ row.type === 'earn' ? '获得' : '使用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="source" label="来源" width="150">
          <template #default="{ row }">
            {{ getSourceName(row.source) }}
          </template>
        </el-table-column>
        <el-table-column prop="amount" label="金额" width="120">
          <template #default="{ row }">
            <span :class="row.type === 'earn' ? 'positive' : 'negative'">
              {{ row.type === 'earn' ? '+' : '-' }}{{ row.amount?.toFixed(2) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="balanceAfter" label="余额" width="120" />
        <el-table-column prop="createdAt" label="时间" />
      </el-table>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import axios from 'axios'

const loading = ref(false)
const transactionLoading = ref(false)
const tableData = ref([])
const transactionList = ref([])
const transactionDialogVisible = ref(false)
const currentUserId = ref(null)

const searchForm = reactive({
  userId: '',
  mobile: ''
})

const pagination = reactive({
  page: 1,
  pageSize: 20,
  total: 0
})

const sourceNames = {
  'friend_consume': '亲友消费帮扶',
  'friend_task': '亲友任务帮扶',
  'consumption_deduct': '消费抵扣',
  'cashback': '消费返利'
}

const getSourceName = (source) => {
  return sourceNames[source] || source || '-'
}

const fetchData = async () => {
  loading.value = true
  try {
    const res = await axios.get('/api/helper-credit/admin/list', {
      params: {
        page: pagination.page,
        pageSize: pagination.pageSize,
        ...searchForm
      }
    })
    if (res.data.code === 200) {
      tableData.value = res.data.data.list || []
      pagination.total = res.data.data.total || 0
    }
  } catch (e) {
    // 模拟数据
    tableData.value = [
      { id: 1, userId: 101, userName: '张三', mobile: '138****0001', balance: 150.00, totalEarned: 200.00, totalUsed: 50.00, createdAt: '2026-03-20 00:00:00' },
      { id: 2, userId: 102, userName: '李四', mobile: '138****0002', balance: 80.00, totalEarned: 100.00, totalUsed: 20.00, createdAt: '2026-03-19 12:00:00' }
    ]
    pagination.total = 2
  } finally {
    loading.value = false
  }
}

const viewTransactions = async (row) => {
  currentUserId.value = row.userId
  transactionDialogVisible.value = true
  transactionLoading.value = true
  
  try {
    const res = await axios.get(`/api/helper-credit/admin/${row.userId}/transactions`)
    if (res.data.code === 200) {
      transactionList.value = res.data.data.list || []
    }
  } catch (e) {
    // 模拟数据
    transactionList.value = [
      { id: 1, type: 'earn', source: 'friend_consume', amount: 10.00, balanceAfter: 150.00, createdAt: '2026-03-20 00:30:00' },
      { id: 2, type: 'earn', source: 'friend_task', amount: 20.00, balanceAfter: 140.00, createdAt: '2026-03-19 18:00:00' }
    ]
  } finally {
    transactionLoading.value = false
  }
}

const handleSearch = () => {
  pagination.page = 1
  fetchData()
}

const resetSearch = () => {
  searchForm.userId = ''
  searchForm.mobile = ''
  handleSearch()
}

const handleSizeChange = (val) => {
  pagination.pageSize = val
  fetchData()
}

const handleCurrentChange = (val) => {
  pagination.page = val
  fetchData()
}

onMounted(() => {
  fetchData()
})
</script>

<style scoped>
.helper-credit-page {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  justify-content: flex-end;
}

.balance {
  font-weight: bold;
  color: #409eff;
}

.earned {
  color: #67c23a;
}

.used {
  color: #e6a23c;
}

.positive {
  color: #67c23a;
}

.negative {
  color: #f56c6c;
}
</style>
